import React, { useState } from 'react';

import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { TextArea } from '../../../../../shared/components/core-ui/text-area/text-area.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { getOneReservations } from '../../../../shared/services/data.service';

export interface MovePaymentModalProps {
  config: {
    isMoving: boolean;
  };
  data: {
    paymentId: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    confirmHandler: (targetReservationId: string, comment: string) => void;
  };
}

export const MovePaymentModal: React.FC<MovePaymentModalProps> = ({
  config: { isMoving },
  data: { paymentId },
  eventHandlers: { closeHandler, confirmHandler }
}) => {
  const isMobileView = useWindowResizer();
  const [reservationIdInput, setReservationIdInput] = useState<string>('');
  const [target, setTarget] = useState<ReservationDetailsUiModel | null>(null);
  const [lookupError, setLookupError] = useState<string>('');
  const [isLookingUp, setIsLookingUp] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');

  const lookupReservation = () => {
    const reservationId = reservationIdInput.trim();
    if (!reservationId) return;
    setIsLookingUp(true);
    setTarget(null);
    setLookupError('');
    getOneReservations(reservationId)
      .then((response) => {
        if (isErrorInfo(response) || !response) {
          setLookupError('Reservation not found');
          return;
        }
        setTarget(response);
      })
      .finally(() => setIsLookingUp(false));
  };

  const canConfirm = !!target && comment.trim().length > 0 && !isMoving;

  return (
    <Modal
      config={{ showModal: true, fullScreen: false, maxWidth: 'sm', showClose: !isMoving }}
      eventHandlers={{
        closeModalHandler: () => {
          if (!isMoving) {
            closeHandler();
          }
        }
      }}
    >
      <div className="flex flex-col gap-5 px-4 pt-2 pb-4 sm:px-10">
        <div>
          <h2 className="font-gilroy-bold text-3xl font-bold text-[#37454d]">
            Move payment {paymentId}
          </h2>
          <hr className="mt-3 border-gray-200" />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base font-semibold text-gray-500" htmlFor="move-payment-target">
            Reservation ID
          </label>
          <div className="flex gap-3">
            <input
              id="move-payment-target"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-xl text-[#37454d] outline-none focus:border-neutral-500"
              value={reservationIdInput}
              disabled={isMoving}
              onChange={(e) => {
                setReservationIdInput(e.target.value);
                setTarget(null);
                setLookupError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  lookupReservation();
                }
              }}
            />
            <Button
              disabled={!reservationIdInput.trim() || isLookingUp || isMoving}
              loading={isLookingUp}
              onClick={lookupReservation}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
            >
              Find
            </Button>
          </div>
          {lookupError && <span className="text-base text-red-600">{lookupError}</span>}
        </div>

        {target && (
          <div className="flex flex-col gap-2 rounded-xl bg-gray-100 p-4 text-base text-[#37454d]">
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Guest</span>
              <span className="truncate">{target.primaryGuestName}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Unit</span>
              <span className="truncate">{target.unitName}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Dates</span>
              <span>
                {new Date(Number(target.checkInDate)).toDateString()} -{' '}
                {new Date(Number(target.checkOutDate)).toDateString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="font-semibold text-gray-500">Status</span>
              <span>{target.status}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-base font-semibold text-gray-500">Comment</span>
          <TextArea
            data={{ defaultValue: '', placeholder: 'Reason for moving this payment' }}
            config={{}}
            eventHandlers={{ onChangeHandler: (value: string) => setComment(value) }}
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 px-4 pt-2 pb-6 sm:flex-row sm:justify-end sm:px-10">
        <Button
          disabled={isMoving}
          onClick={closeHandler}
          type={ButtonType.primaryInverted}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Cancel
        </Button>
        <Button
          disabled={!canConfirm}
          loading={isMoving}
          onClick={() => target && confirmHandler(String(target.id), comment.trim())}
          type={ButtonType.default}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Move payment
        </Button>
      </div>
    </Modal>
  );
};
